/* ============================================================
   addressBookView — CB-3 (31de9d9f). Pure view models for the
   address book: entry validation (mirrors AddressBookService
   rules), checkout chooser state (default preselected, entry vs
   free-form precedence) and row projection for the lists.
   ============================================================ */

import type { AddressBookEntry } from '../types/address';

export const ADDRESS_MIN_LENGTH = 5;
export const ADDRESS_MAX_LENGTH = 300;
export const LABEL_MAX_LENGTH = 50;
export const MAX_ENTRIES = 10;

/** Checkout chooser mode: a book entry or a typed free-form address. */
export type ChooserMode = 'book' | 'custom';

/** Mirror of the server rule: trimmed address 5..300 chars. */
export function addressLineError(address: string): string | null {
  const trimmed = address.trim();
  if (trimmed.length === 0) return null; // optional until the user types
  if (trimmed.length < ADDRESS_MIN_LENGTH)
    return `Адрес — минимум ${ADDRESS_MIN_LENGTH} символов`;
  if (trimmed.length > ADDRESS_MAX_LENGTH)
    return `Адрес — максимум ${ADDRESS_MAX_LENGTH} символов`;
  return null;
}

/** Mirror of the server rule: label trimmed, at most 50 chars. */
export function labelError(label: string): string | null {
  const trimmed = label.trim();
  if (trimmed.length > LABEL_MAX_LENGTH) return `Название — максимум ${LABEL_MAX_LENGTH} символов`;
  return null;
}

/** A row may be submitted when both fields pass validation. */
export function canSubmitEntry(label: string, address: string): boolean {
  const trimmed = address.trim();
  return trimmed.length >= ADDRESS_MIN_LENGTH && addressLineError(address) === null && labelError(label) === null;
}

/** Initial chooser state: default entry preselected; empty book → free-form. */
export function initialChooser(
  entries: AddressBookEntry[],
): { mode: ChooserMode; selectedId: string | null } {
  if (entries.length === 0) return { mode: 'custom', selectedId: null };
  const preferred = entries.find((e) => e.isDefault) ?? entries[0];
  return { mode: 'book', selectedId: preferred.id };
}

/**
 * The address string checkout submits: the selected entry's line in book
 * mode, the trimmed free-form text in custom mode, null when nothing
 * usable was chosen.
 */
export function submitAddress(
  mode: ChooserMode,
  selectedId: string | null,
  entries: AddressBookEntry[],
  customText: string,
): string | null {
  if (mode === 'book') {
    const entry = entries.find((e) => e.id === selectedId);
    return entry ? entry.addressLine : null;
  }
  const trimmed = customText.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Build a create request with server-side trimming applied client-side too. */
export function buildEntryRequest(
  label: string,
  address: string,
  makeDefault: boolean,
): { label: string | null; addressLine: string; isDefault: boolean } {
  const trimmedLabel = label.trim();
  return {
    label: trimmedLabel.length > 0 ? trimmedLabel : null,
    addressLine: address.trim(),
    isDefault: makeDefault,
  };
}

/** Short row title: the label or a neutral fallback. */
export function entryTitle(entry: Pick<AddressBookEntry, 'label'>): string {
  return entry.label.trim().length > 0 ? entry.label.trim() : 'Адрес';
}

/** Row subtitle pieces: postal code (when present) + full line. */
export function entrySubtitle(entry: Pick<AddressBookEntry, 'addressLine' | 'postalCode'>): string {
  return entry.postalCode ? `${entry.postalCode} · ${entry.addressLine}` : entry.addressLine;
}

/** Whether the checkout offers the save-to-book checkbox (new free-form text). */
export function canOfferSaveToBook(
  mode: ChooserMode,
  customText: string,
  entries: AddressBookEntry[],
): boolean {
  if (mode !== 'custom') return false;
  return customText.trim().length >= ADDRESS_MIN_LENGTH && entries.length < MAX_ENTRIES;
}
